import { useRef, useState, type ChangeEvent } from "react";
import type { MusicSource } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { MySongPicker } from "../components/MySongPicker";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useWork } from "../lib/api";
import { navigate } from "../lib/navigation";
import { loadSession, pathForStep } from "../lib/session";
import { useImeSafeInput } from "../lib/ime";

const songCodePattern = /^[A-HJ-NP-Z2-9]{13}$/u;

function durationLabel(seconds: number): string {
  const rounded = Math.max(0, Math.round(seconds));
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, "0")}`;
}

function uploadError(error: unknown): string {
  if (!(error instanceof ApiError)) return "파일을 읽지 못했어요";
  if (error.status === 413) return "파일이 너무 커요";
  if (error.status === 429) return "올리기는 5번까지예요";
  if (error.code === "TOO_LONG") return "3분까지만 올릴 수 있어요";
  if (error.code === "UPLOAD_DISABLED") return "선생님이 파일 올리기를 꺼 두었어요";
  return "파일을 읽지 못했어요";
}

/**
 * 🔑 `picture` = 그림 수업(2026-09-24)의 「노래 붙이기」(D5). 곡 코드·파일 올리기는 영상 수업과
 *    **같은 문**을 쓰고(서버 검사 5회·3분·10MB 동일), 바뀌는 것은 제목·안내·아래 버튼뿐이다.
 *    노래는 선택이라 「노래 없이 건너뛰기」가 있다.
 */
export function SongSourcePage({ workId, variant = "mv" }: { workId: string; variant?: "mv" | "picture" }) {
  const query = useWork(workId);
  const session = loadSession();
  const picture = variant === "picture";
  const [code, setCode] = useState("");
  // 한글 자판 상태로 치면 조합 중 글자가 날아간다 — 거르기·대문자 변환은 조합이 끝난 뒤에만 한다(lib/ime.ts)
  const codeIme = useImeSafeInput(setCode, (value) => value.replace(/[^A-Za-z2-9]/gu, "").toUpperCase());
  const [busy, setBusy] = useState<"code" | "upload" | "next" | "skip" | "back" | "pick" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  if (query.loading) return <LoadingPage label="노래 선택을 불러오는 중..." />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "노래 선택을 불러오지 못했습니다."} />;
  }
  const source = query.data.work.music_source;

  const importSong = async (): Promise<void> => {
    const normalized = code.trim().toUpperCase();
    setCode(normalized);
    if (!songCodePattern.test(normalized)) {
      setError("코드를 확인해 주세요");
      return;
    }
    setBusy("code");
    setError(null);
    try {
      await apiRequest<{ music_source: MusicSource }>(
        `/api/works/${encodeURIComponent(workId)}/import-song`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ code: normalized }),
        },
      );
      await query.refresh();
    } catch (caught) {
      setError(caught instanceof ApiError && caught.status === 429
        ? "잠깐 쉬었다가 다시 해요"
        : "코드를 확인해 주세요");
    } finally {
      setBusy(null);
    }
  };

  const uploadSong = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy("upload");
    setError(null);
    const form = new FormData();
    form.set("audio", file);
    try {
      await apiRequest<{ music_source: MusicSource }>(
        `/api/works/${encodeURIComponent(workId)}/upload-song`,
        { method: "POST", body: form },
      );
      await query.refresh();
    } catch (caught) {
      setError(uploadError(caught));
    } finally {
      setBusy(null);
    }
  };

  const next = async (): Promise<void> => {
    if (!source) return;
    const step = picture ? "pic_done" : "story";
    setBusy("next");
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step, gifts_v: 2 }),
      });
      navigate(pathForStep(workId, step));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "다음 단계로 가지 못했어요.");
    } finally {
      setBusy(null);
    }
  };

  /** 그림 수업 — 그림 화면으로 돌아간다(2026-10-07 jery: 그림을 만든 뒤 돌아갈 길이 없었다). 붙인 노래는 그대로 둔다. */
  const back = async (): Promise<void> => {
    setBusy("back");
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "pic_image", gifts_v: 2 }),
      });
      navigate(pathForStep(workId, "pic_image"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "이전 단계로 돌아가지 못했어요.");
    } finally {
      setBusy(null);
    }
  };

  /** 그림 수업 — 노래 없이 작품 카드로. 붙여 둔 노래가 있으면 뗀다(「노래 없이」가 사실이 되게). */
  const skip = async (): Promise<void> => {
    setBusy("skip");
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "pic_done", ...(source ? { clear_music: true } : {}), gifts_v: 2 }),
      });
      navigate(pathForStep(workId, "pic_done"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "다음 단계로 가지 못했어요.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <StudioShell currentStep={picture ? 3 : 1} imageRegenRemaining={query.data.image_regen_remaining}>
      <div className="mv-page mv-song-page">
        {picture ? (
          <header className="mv-heading">
            <span className="mv-kicker">그림 3 · 노래 붙이기 (선택)</span>
            <h1 tabIndex={-1}>내 그림에 노래를 붙여요</h1>
            <p>음악 만들기 수업에서 만든 곡 코드를 가져오거나, 내 음원 파일을 올릴 수 있어요. 안 붙여도 괜찮아요.</p>
          </header>
        ) : (
          <header className="mv-heading">
            <span className="mv-kicker">MV 1 · 노래 선택</span>
            <h1 tabIndex={-1}>뮤직비디오에 쓸 노래를 골라요</h1>
            <p>음악 만들기 수업에서 만든 곡 코드를 가져오거나, 내 음원 파일을 올릴 수 있어요.</p>
          </header>
        )}
        {source ? (
          <section className="mv-selected-source" aria-live="polite">
            <span aria-hidden="true">♪</span>
            <div>
              <strong>{source.title}</strong>
              <small>{source.type === "imported" ? "곡 코드로 가져옴" : "내 파일"} · {durationLabel(source.duration_seconds)}</small>
            </div>
            <b>선택됨 ✓</b>
          </section>
        ) : null}
        <MySongPicker
          workId={workId}
          selectedTrackId={source?.type === "imported" ? source.source_track_id : null}
          disabled={busy !== null}
          onBusyChange={(picking) => setBusy(picking ? "pick" : null)}
          onImported={() => query.refresh()}
        />
        <div className="mv-source-grid">
          <section className="mv-card">
            <h2>내 곡 코드 가져오기</h2>
            <p>음악 만들기에서 완성한 노래의 13자리 코드를 입력해요.</p>
            <label htmlFor="song-code">곡 코드</label>
            <input
              id="song-code"
              className="song-code-input"
              value={code}
              maxLength={13}
              placeholder="ABCD2345EFGH6"
              autoCapitalize="characters"
              {...codeIme}
            />
            <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void importSong()}>
              {busy === "code" ? <InlineSpinner label="가져오는 중..." /> : "코드로 가져오기"}
            </button>
          </section>
          <section className="mv-card">
            <h2>음원 파일 올리기</h2>
            <p>MP3·M4A·WAV, 10MB 이하, 최대 3분까지 올릴 수 있어요.</p>
            {/*
              🔑 «올릴 수 없어요»(단정) 가 아니라 «올리지 않기로 약속해요»(규범) 다 — 2026-08-12.
                 서버는 저작권을 **한 줄도 검사하지 않는다**(upload.ts 가 보는 것 = mv 수업·5회·mime·3분·10MB).
                 지문 대조를 안 붙이는 건 설계 결정이다(PLAN-music-studio-pr2 D4 「추가 자동 검증 도입 안 함」).
                 그런데 문구만 단정형이라 «시스템이 막아준다» 로 읽혔다 — 막아주지 않는데 막아준다고 적어 두면
                 사고가 났을 때 교사가 화면을 근거로 삼는다. 문구를 사실 쪽으로 내렸다.

              🔑 «갤러리·감상회에서만 재생돼요» 도 같은 이유로 내렸다 — 2026-08-13.
                 그 문장은 **사실이 아니었다.** 올린 곡으로 만든 영상은 완성 화면(`FinalPage`)에서
                 「완성작 다시 보기」로 **그 자리에서 재생된다.** 갤러리 동의를 껐어도 재생된다.
                 실제로 막혀 있는 것은 재생 장소가 아니라 **내려받기**다
                 (`edit.ts:299` `download_allowed = musicSource.type !== "upload"` → MP4 저장 버튼 미노출).
                 계획서도 이미 그렇게 적어 뒀다 — PLAN-music-studio-pr3 D6 「강제 수준 = UX 억제 +
                 단기 TTL 서명 URL, **재생 URL 바이트 접근 자체는 차단 불가함을 계약에 명시**」.
                 «어디서만 재생된다» 는 지킬 수 없는 약속이고, «저장은 안 된다» 는 실제로 지켜진다.
            */}
            <div className="copyright-note">
              가수의 노래처럼 <strong>저작권이 있는 음악은 올리지 않기로 약속해요.</strong> 내가 만든 곡, 우리 반이 녹음한 소리만 올려요.
              {picture
                ? <> 붙인 노래는 작품 카드와 학교 전시 페이지에서 그림을 누르면 나와요.</>
                : <> 올린 곡으로 만든 영상은 <strong>MP4 로 저장할 수 없어요</strong> — 완성 화면과 우리 반 갤러리·감상회에서 볼 수 있어요.</>}
            </div>
            <input
              ref={fileInput}
              className="sr-only"
              type="file"
              accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav"
              onChange={(event) => void uploadSong(event)}
            />
            {/*
              🔑 파일 올리기는 **항상 열려 있다**(2026-08-11). 예전엔 교사가 수업을 만들 때
                 「학생 음원 업로드 허용」을 켜야만 눌리는 버튼이었고, 기본값이 꺼짐이라
                 대부분의 수업에서 이 버튼은 회색이었다. 스위치 자체를 없앴다.
            */}
            <button
              className="button-secondary"
              type="button"
              disabled={busy !== null}
              onClick={() => fileInput.current?.click()}
            >
              {busy === "upload" ? <InlineSpinner label="올리는 중..." /> : "파일 선택"}
            </button>
          </section>
        </div>
        {error ? <InlineError>{error}</InlineError> : null}
        {picture ? (
          <footer className="mv-footer">
            <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void back()}>
              {busy === "back" ? <InlineSpinner label="돌아가는 중..." /> : "이전"}
            </button>
            <button className="button-ghost" type="button" disabled={busy !== null} onClick={() => void skip()}>
              {busy === "skip" ? <InlineSpinner label="저장 중..." /> : "노래 없이 건너뛰기"}
            </button>
            <button className="button-primary" type="button" disabled={!source || busy !== null} onClick={() => void next()}>
              {busy === "next" ? <InlineSpinner label="저장 중..." /> : "이 노래로 완성"}
            </button>
          </footer>
        ) : (
          <footer className="mv-footer">
            <span>곡을 바꾸면 나중에 만든 컷 시간이 다시 계산될 수 있어요.</span>
            <button className="button-primary" type="button" disabled={!source || busy !== null} onClick={() => void next()}>
              {busy === "next" ? <InlineSpinner label="저장 중..." /> : "이 노래로 계속"}
            </button>
          </footer>
        )}
      </div>
    </StudioShell>
  );
}
